import React, { useEffect, useState } from 'react';
import Loader from '../../common/Loader';
import PageTitle from '../../components/PageTitle';
import { Grid, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText, Chip, Card, CardContent } from '@mui/material';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { apiConstants, getGeneric, postGeneric } from '../../common/constants';
import { FormProvider, RHFTextField } from '../../common/hook-form';
import { useForm } from 'react-hook-form';
import { formatDate } from '../../common/util';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import SyncIcon from '@mui/icons-material/Sync';
import CopyIcon from '../../common/CopyIcon';


const InsuranceList = ({ status }) => {
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [list, setList] = useState([]);
    const [openLogsDialog, setOpenLogsDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [andfilter, setAndfilter] = useState([]);
    const [toggleConfirm, setToggleConfirm] = useState(false);
    const [selectedOperation, setSelectedOperation] = useState(null);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [reload, setReload] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    const handleOpenLogs = (row) => {
        setSelectedRow(row);
        setOpenLogsDialog(true);
    };

    const handleCloseLogs = () => {
        setOpenLogsDialog(false);
        setSelectedRow(null);
    };

    const handleClose = () => {
        setToggleConfirm(false);
        setSelectedOperation(null);
        setSelectedRowData(null);
    };

    const handleYes = (operationType = selectedOperation) => {
        setIsLoading(true);
        const payload = {
            "proposalRefString": selectedRowData?.proposalId,
        };
        console.log(operationType, 'payload');
        if (operationType === 'approve') {
            postGeneric(apiConstants.APPROVE_INSURANCE_PROPOSAL, payload)
                .then((res) => {
                    handleClose();
                    setReload(reload + 1);
                    enqueueSnackbar('Proposal approved successfully', { variant: 'success' });
                })
                .catch((err) => {
                    console.log(err, 'err');
                    enqueueSnackbar(err?.response?.data?.message || 'Some error occurred', { variant: 'error' });
                    handleClose();
                }).finally(() => {
                    setIsLoading(false);
                });
        } else if (operationType === 'reject') {
            // const payload = {
            //     "proposalRefString": selectedRowData?.proposalId,
            // };
            postGeneric(apiConstants.REJECT_INSURANCE_PROPOSAL, payload)
                .then((res) => {
                    handleClose();
                    enqueueSnackbar('Proposal rejected successfully', { variant: 'success' });
                    setReload(reload + 1);
                })
                .catch((err) => {
                    handleClose();
                }).finally(() => {
                    setIsLoading(false);
                });
        }
        else if (operationType === 'resync') {
            setIsLoading(true);
            getGeneric(`${apiConstants.RESYNC_INSURANCE_PROPOSAL}?proposalRefString=${selectedRowData?.proposalId}`)
                .then((res) => {
                    handleClose();
                    enqueueSnackbar('Proposal resynced successfully', { variant: 'success' });
                    setReload(reload + 1);
                })
                .catch((err) => {
                    handleClose();
                    enqueueSnackbar(err?.response?.data?.message || 'Some error occurred', { variant: 'error' });
                }).finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const handleOperation = (row, type) => {
        setSelectedRowData(row);
        if (type === 'resync') {
            setSelectedOperation(type);
            // Pass the operation type directly to avoid state update race condition
            handleYes(type);
        } else {
            setSelectedOperation(type);
            setToggleConfirm(true);
        }
    };

    const handleRefetch = (row) => {
        setIsLoading(true);
        getGeneric(`${apiConstants.FETCH_INSURANCE_CERTIFICATE}?proposalRefString=${row?.proposalId}`)
            .then((res) => {
                enqueueSnackbar('Certificate fetched successfully', { variant: 'success' });
                setReload(reload + 1);
            })
            .catch((err) => {
                console.log(err, 'err');
            }).finally(() => {
                setIsLoading(false);
            })
    }

    useEffect(() => {
        setIsLoading(true);
        const payload = {
            "collection": "proposals",
            "andfilter": andfilter,
            // limit: 10000
        }
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, payload).then((res) => {
            const filteredList = res?.data?.data?.filter((item) => status.includes(item?.proposalStatus))
            setList(filteredList)
        }).catch((err) => {
            console.log(err, 'err');
        }).finally(() => {
            setIsLoading(false);
        })
    }, [status, reload, andfilter])

    const getStatusColor = (status) => {
        switch (status) {
            case 'DRAFT':
                return 'default';
            case 'APPROVED':
                return 'success';
            case 'CANCEL':
                return 'error';
            case 'CERTIFICATE_GENERATED':
                return 'info';
            default:
                return 'default';
        }
    };

    const columns = [
        {
            field: 'awbNumber', headerAlign: "left", hideSortIcons: true, headerName: 'AWB Number', flex: 0.8, renderCell: (params) => {
                const { row } = params;
                return (
                    <Grid container alignItems="center" wrap="nowrap">
                        <a
                            style={{ color: "#3783e7", textDecoration: "underline" }}
                            href={`/order-detail/${row?.awbNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {row?.awbNumber}
                        </a>
                        <CopyIcon text={row?.awbNumber} />
                    </Grid>
                )
            }
        },
        {
            field: 'createdInfo', headerAlign: "left", hideSortIcons: true, headerName: 'Created User & Time', flex: 1,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Grid container direction="column">
                        <Grid item>{row?.createdByUser}</Grid>
                        <Grid item style={{ fontSize: '0.8rem', color: '#666' }}>{formatDate(row?.createdDate)}</Grid>
                    </Grid>
                );
            }
        },
        {
            field: 'orderId', headerAlign: "left", hideSortIcons: true, headerName: 'Order ID', flex: 0.8
        },
        {
            field: 'proposalStatus', headerAlign: "left", hideSortIcons: true, headerName: 'Proposal Status & Time', flex: 1.2,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Grid container direction="column" spacing={1}>
                        <Grid item>
                            <Chip
                                label={row?.proposalStatus}
                                color={getStatusColor(row?.proposalStatus)}
                                size="small"
                            />
                        </Grid>
                        <Grid item style={{ fontSize: '0.8rem', color: '#666' }}>
                            {formatDate(row?.proposalTime)}
                        </Grid>
                    </Grid>
                );
            }
        },
        {
            field: 'policyCertificateLink', headerAlign: "left", hideSortIcons: true, headerName: 'Certificate', flex: 0.6, renderCell: (params) => {
                const { row } = params;
                if (row?.policyCertificateLocalLink || row?.policyCertificateLink) {
                    return (
                        <a
                            href={row?.policyCertificateLocalLink || row?.policyCertificateLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#1976d2',
                                textDecoration: 'underline',
                                cursor: 'pointer'
                            }}
                        >
                            View
                        </a>
                    );
                } else if (!row?.policyCertificateLocalLink && !row?.policyCertificateLink &&
                    (row?.proposalStatus === 'APPROVED' || row?.proposalStatus === 'CERTIFICATE_GENERATED')) {
                    return (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleRefetch(row)}
                        >
                            Generate
                        </Button>
                    );
                }
                return '-';
            },
        },
        {
            field: 'logs', headerAlign: "left", hideSortIcons: true, headerName: 'Action', flex: 0.8, renderCell: (params) => {
                const { row } = params;
                return (
                    <>
                        <Tooltip title="Status Log">
                            <IconButton size="small" varient='contained'
                                onClick={() => handleOpenLogs(row?.statusHistory)}
                            >
                                <HistoryIcon fontSize="small" color='primary' />
                            </IconButton>
                        </Tooltip>
                        {status === 'DRAFT' && (
                            <>
                                <Tooltip title="Resync">
                                    <IconButton size="small" varient='contained'
                                        onClick={() => handleOperation(row, 'resync')}
                                    >
                                        <SyncIcon fontSize="small" color='warning' />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Approve">
                                    <IconButton size="small" varient='contained'
                                        onClick={() => handleOperation(row, 'approve')}
                                    >
                                        <CheckCircleIcon fontSize="small" color='success' />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                    <IconButton size="small" varient='contained'
                                        onClick={() => handleOperation(row, 'reject')}
                                    >
                                        <CancelIcon fontSize="small" color='error' />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </>
                )
            },
        },

    ];

    const methods = useForm({
        defaultValues: {
            awb: '',
        }
    });
    const {
        handleSubmit,
        reset,
        // setValue,
        // formState: { isSubmitting }
    } = methods;

    const onSubmit = (data) => {
        const andfilter = [];
        if (data.awb) {
            andfilter.push({ "field": "awbNumber", "operator": "eq", "value": data.awb });
        }
        setAndfilter(andfilter);
    }


    return (
        <React.Fragment>
            <PageTitle>Insurance Config</PageTitle>
            {isLoading && <Loader />}
            <Card sx={{ mb: 2, mt: 2 }}>
                <CardContent >
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4}>
                                <RHFTextField name="awb" label="AWB Number" color="secondary" />
                            </Grid>
                            <Grid item xs={4}>
                                <Button type="submit" variant="contained" color="primary" size='small'>Filter</Button>
                                <Button sx={{ ml: 1 }} type="button" variant="contained" color="error" size='small' onClick={() => {
                                    reset()
                                    setAndfilter([])
                                }}>Reset</Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
            <Grid item container spacing={4}>
                <Grid item xs={12}>
                    <ProzoDataGrid
                        columns={columns}
                        autoHeight={true}
                        rows={list.length > 0 ? list.map((item, index) => ({
                            ...item,
                            id: index,
                        })) : []}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        // hideFooterPagination={true}
                        // rowCount={Number.MAX_VALUE}
                        rowCount={list?.length}
                        paginationMode="client"
                        // hideCountNumber={true}
                        getRowHeight={() => 60}
                        getRowId={(row) => row.id} />
                </Grid>
            </Grid>

            <Dialog
                open={openLogsDialog}
                onClose={handleCloseLogs}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Status Logs</DialogTitle>
                <DialogContent>
                    <ProzoDataGrid
                        columns={[
                            {
                                field: 'status',
                                headerName: 'Status',
                                flex: 1,
                            },
                            {
                                field: 'proposalId',
                                headerName: 'Proposal ID',
                                flex: 1,
                            },
                            {
                                field: 'statusTime',
                                headerName: 'Status Time',
                                flex: 1,
                                renderCell: (params) => formatDate(params.value)
                            }
                        ]}
                        rows={selectedRow?.length > 0 ? selectedRow.map((item, index) => ({
                            ...item,
                            id: index,
                        })) : []}
                        autoHeight
                        getRowId={(row) => row.id}
                        getRowHeight={() => 40}
                        hideFooterPagination={true}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLogs}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={toggleConfirm}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    {`Confirm ${selectedOperation === 'approve' ? 'Approval' : 'Rejection'}`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <Typography variant="subtitle1">
                            {`Are you sure you want to ${selectedOperation === 'approve' ? 'approve' : 'reject'} this proposal?`}
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={isLoading} onClick={() => handleYes(selectedOperation)} variant="contained" size="small" color="primary">
                        Yes
                    </LoadingButton>
                    <Button onClick={handleClose} variant="contained" size="small" color="error">
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
export default InsuranceList;